<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import WhatsAppCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';
import WhatsAppCampaignsBanner from 'dashboard/components-next/Campaigns/WhatsAppCampaignsBanner.vue';
import CampaignsDashboard from 'dashboard/components-next/Campaigns/CampaignsDashboard.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import { useStore } from 'vuex';

const { t } = useI18n();
const getters = useStoreGetters();
const store = useStore();

const selectedCampaign = ref(null);
const [showWhatsAppCampaignDialog, toggleWhatsAppCampaignDialog] = useToggle();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);
const campaignMeta = computed(() => store.getters['campaigns/getMeta']);

const confirmDeleteCampaignDialogRef = ref(null);

const searchQuery = ref('');
const selectedStatus = ref('all');
const dateRange = ref({ from: null, to: null });
const currentPage = ref(1);
const itemsPerPage = ref('10');

const itemsPerPageOptions = [
  { label: 'Mostrar 10', value: '10' },
  { label: 'Mostrar 20', value: '20' },
  { label: 'Mostrar 50', value: '50' },
  { label: 'Mostrar 100', value: '100' }
];

const statusOptions = [
  { label: 'Todos los estados', value: 'all' },
  { label: 'Completada', value: 'completed' },
  { label: 'En Progreso', value: 'in_progress' },
  { label: 'Fallida', value: 'failed' },
  { label: 'Pausada', value: 'paused' }
];

const fetchCampaigns = () => {
  const params = { page: currentPage.value, per_page: parseInt(itemsPerPage.value) };
  if (searchQuery.value) params.search = searchQuery.value;
  if (selectedStatus.value !== 'all') params.status = selectedStatus.value;
  if (dateRange.value.from && dateRange.value.to) {
    params.from = dateRange.value.from;
    params.to = dateRange.value.to;
  }
  store.dispatch('campaigns/get', params);
};

// watch for changes in filters
watch([searchQuery, selectedStatus, dateRange, itemsPerPage], () => {
  currentPage.value = 1;
  fetchCampaigns();
}, { deep: true });

watch(currentPage, () => {
  fetchCampaigns();
});

let pollingInterval = null;

onMounted(() => {
  fetchCampaigns();
  
  pollingInterval = setInterval(() => {
    fetchCampaigns();
  }, 10000); // 10 seconds
});

onUnmounted(() => {
  if (pollingInterval) clearInterval(pollingInterval);
});

const handlePageChange = (page) => {
  currentPage.value = page;
};

const WhatsAppCampaigns = computed(
  () => getters['campaigns/getWhatsAppCampaigns'].value
);

const hasNoWhatsAppCampaigns = computed(
  () => WhatsAppCampaigns.value?.length === 0 && !isFetchingCampaigns.value && !searchQuery.value && selectedStatus.value === 'all'
);

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.WHATSAPP.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
    @click="toggleWhatsAppCampaignDialog()"
    @close="toggleWhatsAppCampaignDialog(false)"
  >
    <template #action>
      <WhatsAppCampaignDialog
        v-if="showWhatsAppCampaignDialog"
        @close="toggleWhatsAppCampaignDialog(false)"
      />
    </template>
    
    <WhatsAppCampaignsBanner />
    
    <CampaignsDashboard :filters="dateRange" />

    <div class="flex flex-col md:flex-row gap-4 mb-6">
      <div class="flex-1">
        <Input v-model="searchQuery" type="search" placeholder="Buscar campaña por nombre..." icon="i-lucide-search" />
      </div>
      <div class="w-full md:w-48">
        <ComboBox v-model="selectedStatus" :options="statusOptions" placeholder="Estado" />
      </div>
      <div class="w-full md:w-40">
        <ComboBox v-model="itemsPerPage" :options="itemsPerPageOptions" placeholder="Mostrar" />
      </div>
      <div class="flex gap-2 w-full md:w-auto items-center">
        <input type="date" v-model="dateRange.from" class="h-10 px-3 rounded-lg border border-n-slate-3 bg-white dark:bg-n-slate-2 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-n-teal-9/50" />
        <span class="text-n-slate-11 text-sm">-</span>
        <input type="date" v-model="dateRange.to" class="h-10 px-3 rounded-lg border border-n-slate-3 bg-white dark:bg-n-slate-2 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-n-teal-9/50" />
      </div>
    </div>

    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div v-else-if="!hasNoWhatsAppCampaigns" class="flex flex-col gap-4">
      <CampaignList
        :campaigns="WhatsAppCampaigns"
        @delete="handleDelete"
      />
      <div class="flex justify-center mt-6 mb-10" v-if="campaignMeta && campaignMeta.total_pages > 1">
        <div class="flex gap-2 items-center">
          <Button :disabled="currentPage === 1" @click="handlePageChange(currentPage - 1)" icon="i-lucide-chevron-left" variant="outline" />
          <span class="text-sm font-medium text-n-slate-11">Página {{ currentPage }} de {{ campaignMeta.total_pages }}</span>
          <Button :disabled="currentPage === campaignMeta.total_pages" @click="handlePageChange(currentPage + 1)" icon="i-lucide-chevron-right" variant="outline" />
        </div>
      </div>
    </div>
    <WhatsAppCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>
